import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import { deviceFlow, homes as homesApi, type Home, type DeviceFlowSession } from '../lib/api.js';
import { Button } from '../components/ui/Button.js';
import { useAuth } from '../context/AuthContext.js';

function parseCodeFromUrl(): string {
  const params = new URLSearchParams(window.location.search);
  return params.get('user_code') ?? '';
}

export default function ActivateBridgePage() {
  const [, navigate] = useLocation();
  const { accessToken, isLoading } = useAuth();
  const [userCode, setUserCode] = useState(parseCodeFromUrl());
  const [session, setSession] = useState<DeviceFlowSession | null>(null);
  const [homes, setHomes] = useState<Home[]>([]);
  const [homeId, setHomeId] = useState<string>('');
  const [label, setLabel] = useState('Home Assistant bridge');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState<'enter-code' | 'confirm' | 'approved' | 'denied'>(
    parseCodeFromUrl() ? 'confirm' : 'enter-code',
  );

  // Redirect unauthed users to login, preserving the code.
  useEffect(() => {
    if (isLoading) return;
    if (!accessToken) {
      const code = parseCodeFromUrl();
      const next = code ? `/activate?user_code=${encodeURIComponent(code)}` : '/activate';
      navigate(`/login?next=${encodeURIComponent(next)}`);
    }
  }, [accessToken, isLoading, navigate]);

  // Load homes once signed in.
  useEffect(() => {
    if (!accessToken) return;
    homesApi.list().then(
      (res) => {
        setHomes(res.homes);
        if (res.homes[0]) setHomeId(res.homes[0].id);
      },
      () => setError('Could not load homes.'),
    );
  }, [accessToken]);

  // Auto-lookup when we have a code in the URL. phase/userCode only.
  useEffect(() => {
    if (!accessToken) return;
    if (phase !== 'confirm') return;
    if (!userCode) return;
    if (session) return;
    void doLookup(userCode);
  }, [accessToken, phase, userCode, session]);

  async function doLookup(code: string) {
    setError(null);
    setBusy(true);
    try {
      const res = await deviceFlow.lookup(code.trim());
      if (res.session.status === 'expired') {
        setError('That code has expired. Restart the bridge to get a fresh code.');
        setSession(null);
        setPhase('enter-code');
        return;
      }
      if (res.session.status !== 'pending') {
        setError('That code is no longer waiting for approval.');
        setSession(null);
        setPhase('enter-code');
        return;
      }
      setSession(res.session);
      setUserCode(res.session.userCode);
      setPhase('confirm');
    } catch (err) {
      const e = err as { status?: number };
      if (e.status === 404) setError("That code doesn't match a pending bridge.");
      else setError('Could not look up that code. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  const haUrl = useMemo(() => {
    const m = session?.metadata;
    if (m && typeof m['haUrl'] === 'string') return m['haUrl'] as string;
    return null;
  }, [session]);

  async function onApprove() {
    if (!session) return;
    if (!homeId) {
      setError('Pick a home for this bridge first.');
      return;
    }
    if (!label.trim()) {
      setError('Give this bridge a name so you can recognize it.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await deviceFlow.approve(session.userCode, homeId, label.trim());
      setPhase('approved');
    } catch (err) {
      const e = err as { status?: number; message?: string };
      if (e.status === 403) setError('You are not an owner or admin of that home.');
      else if (e.status === 409) setError('That code was already used or cancelled.');
      else setError('Could not approve the bridge. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  async function onDeny() {
    if (!session) return;
    if (!confirm("Cancel pairing? The bridge will stop waiting and you'll need to restart it.")) {
      return;
    }
    setBusy(true);
    try {
      await deviceFlow.deny(session.userCode);
      setPhase('denied');
    } catch {
      setError('Could not cancel pairing.');
    } finally {
      setBusy(false);
    }
  }

  if (isLoading || !accessToken) {
    return (
      <div
        className="flex items-center justify-center h-screen"
        style={{ color: 'var(--ac-text-muted)' }}
      >
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-12">
      <div
        className="w-full max-w-md rounded-xl p-8 shadow-lg"
        style={{ background: 'var(--ac-surface)', border: '1px solid var(--ac-border)' }}
      >
        <h1 className="text-2xl font-semibold mb-2">Connect a bridge</h1>
        <p className="mb-6" style={{ color: 'var(--ac-text-muted)' }}>
          Your bridge is asking to link with your AetherCasa account.
        </p>

        {error && (
          <div
            className="mb-4 rounded-md px-3 py-2 text-sm"
            style={{
              background: 'var(--ac-error-bg, rgba(255,90,90,0.08))',
              color: 'var(--ac-error, #ff5a5a)',
              border: '1px solid var(--ac-error, #ff5a5a)',
            }}
          >
            {error}
          </div>
        )}

        {phase === 'enter-code' && (
          <div>
            <label className="block text-sm mb-2" htmlFor="user-code">
              Enter the code from your bridge logs:
            </label>
            <input
              id="user-code"
              value={userCode}
              onChange={(e) => setUserCode(e.target.value.toUpperCase())}
              placeholder="XXXX-XXXX"
              autoComplete="off"
              autoFocus
              className="w-full rounded-md px-3 py-2 mb-4 font-mono tracking-widest text-center text-lg"
              style={{
                background: 'var(--ac-input-bg, transparent)',
                border: '1px solid var(--ac-border)',
                color: 'var(--ac-text)',
              }}
            />
            <Button onClick={() => void doLookup(userCode)} disabled={busy || !userCode.trim()}>
              {busy ? 'Checking…' : 'Continue'}
            </Button>
          </div>
        )}

        {phase === 'confirm' && session && (
          <div>
            <div
              className="mb-5 rounded-md p-3 text-sm font-mono"
              style={{
                background: 'var(--ac-surface-raised, rgba(255,255,255,0.03))',
                border: '1px solid var(--ac-border)',
              }}
            >
              <div className="mb-1">
                Code: <span style={{ color: 'var(--ac-text)' }}>{session.userCode}</span>
              </div>
              <div className="mb-1" style={{ color: 'var(--ac-text-muted)' }}>
                Bridge version: {session.bridgeVersion}
              </div>
              <div style={{ color: 'var(--ac-text-muted)' }}>
                Fingerprint: {session.fingerprint.slice(0, 16)}…
              </div>
              {haUrl && (
                <div style={{ color: 'var(--ac-text-muted)' }}>Home Assistant: {haUrl}</div>
              )}
            </div>

            <label className="block text-sm mb-2" htmlFor="home-select">
              Link to home:
            </label>
            {homes.length === 0 ? (
              <p className="mb-4 text-sm" style={{ color: 'var(--ac-text-muted)' }}>
                You don&apos;t have any homes yet.{' '}
                <a className="underline" href="/settings/homes">
                  Create one first.
                </a>
              </p>
            ) : (
              <select
                id="home-select"
                value={homeId}
                onChange={(e) => setHomeId(e.target.value)}
                className="w-full rounded-md px-3 py-2 mb-4"
                style={{
                  background: 'var(--ac-input-bg, transparent)',
                  border: '1px solid var(--ac-border)',
                  color: 'var(--ac-text)',
                }}
              >
                {homes.map((h) => (
                  <option key={h.id} value={h.id}>
                    {h.name}
                  </option>
                ))}
              </select>
            )}

            <label className="block text-sm mb-2" htmlFor="label-input">
              Bridge name:
            </label>
            <input
              id="label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="w-full rounded-md px-3 py-2 mb-5"
              style={{
                background: 'var(--ac-input-bg, transparent)',
                border: '1px solid var(--ac-border)',
                color: 'var(--ac-text)',
              }}
            />

            <div className="flex gap-2">
              <Button onClick={() => void onApprove()} disabled={busy || homes.length === 0}>
                {busy ? 'Approving…' : 'Approve'}
              </Button>
              <Button variant="ghost" onClick={() => void onDeny()} disabled={busy}>
                Cancel pairing
              </Button>
            </div>

            <p className="mt-4 text-xs" style={{ color: 'var(--ac-text-muted)' }}>
              Confirm the code in your bridge logs matches what&apos;s shown above before clicking
              Approve.
            </p>
          </div>
        )}

        {phase === 'approved' && (
          <div>
            <p className="mb-4">
              Your bridge is linked. It should connect within a few seconds — check{' '}
              <a className="underline" href="/settings/homes">
                Settings → Homes
              </a>{' '}
              to watch it come online.
            </p>
            <Button onClick={() => navigate('/settings/homes')}>Go to homes</Button>
          </div>
        )}

        {phase === 'denied' && (
          <div>
            <p className="mb-4">Pairing cancelled. Restart the bridge to try again.</p>
            <Button onClick={() => navigate('/')}>Back to dashboard</Button>
          </div>
        )}
      </div>
    </div>
  );
}
